import { useEffect, useMemo, useRef, useState } from 'react'
import type { TenantBootstrapModel } from '@unified-lk/tenant-config'
import { ToothChart } from './ToothChart'
import { PublicPaymentWidget } from './PublicPaymentWidget'

// ─── Format helpers ───────────────────────────────────────────────────────────

function formatDate(value?: string | null): string {
  if (!value) return 'Не указано'
  try { return new Date(value).toLocaleDateString('ru-RU') } catch { return value }
}

function formatDateTime(value?: string | null): string {
  if (!value) return 'Не указано'
  try { return new Date(value).toLocaleString('ru-RU') } catch { return value }
}

function formatCurrency(value?: number | null): string {
  if (value === null || value === undefined) return '—'
  return value.toLocaleString('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 })
}

// ─── Countdown ───────────────────────────────────────────────────────────────

interface CountdownParts { days: number; hours: number; minutes: number; seconds: number; expired: boolean }

function calcCountdown(deadline: string): CountdownParts | null {
  const target = new Date(deadline)
  if (!Number.isFinite(target.getTime())) return null
  const diffMs = target.getTime() - Date.now()
  if (diffMs <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true }
  const totalSeconds = Math.floor(diffMs / 1000)
  return {
    days: Math.floor(totalSeconds / 86400),
    hours: Math.floor((totalSeconds % 86400) / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
    expired: false,
  }
}

function useCountdown(deadline: string | null | undefined): CountdownParts | null {
  const [parts, setParts] = useState<CountdownParts | null>(() => (deadline ? calcCountdown(deadline) : null))
  useEffect(() => {
    if (!deadline) { setParts(null); return }
    setParts(calcCountdown(deadline))
    const id = window.setInterval(() => setParts(calcCountdown(deadline)), 1000)
    return () => window.clearInterval(id)
  }, [deadline])
  return parts
}

function CountdownDigit({ value, accent }: { value: number; accent: string }) {
  return (
    <div className="flex flex-col items-center">
      <div
        className="flex h-12 w-14 items-center justify-center overflow-hidden rounded-xl border bg-white shadow-sm"
        style={{ borderColor: accent + '44' }}
      >
        <span className="text-2xl font-bold tabular-nums text-slate-900">{String(value).padStart(2, '0')}</span>
      </div>
    </div>
  )
}

function OfferCountdownBadge({
  deadline,
  accent,
  totalAmount,
  hidePrices,
}: {
  deadline: string | null | undefined
  accent: string
  totalAmount?: number | null
  hidePrices?: boolean
}) {
  const parts = useCountdown(deadline)
  if (!deadline) return null
  const futureTotal =
    !hidePrices && typeof totalAmount === 'number' && Number.isFinite(totalAmount)
      ? Math.round(totalAmount * 1.3)
      : null
  return (
    <section className="rounded-[28px] border border-slate-200/70 bg-white p-6 shadow-[0_18px_42px_rgba(15,23,42,0.08)]">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-bold uppercase tracking-[0.14em]" style={{ color: accent }}>Фиксация предложения</p>
          <h2 className="mt-1 text-xl font-semibold text-slate-900">Цена зафиксирована до {formatDate(deadline)}</h2>
          <div className="mt-4">
            {parts?.expired ? (
              <div className="inline-flex items-center gap-2 rounded-full border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-700">
                Срок предложения истёк
              </div>
            ) : parts ? (
              <div className="flex items-end gap-1">
                {parts.days > 0 && (
                  <>
                    <div className="flex flex-col items-center gap-1">
                      <CountdownDigit value={parts.days} accent={accent} />
                      <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">дней</span>
                    </div>
                    <span className="mb-5 text-xl font-bold" style={{ color: accent }}>:</span>
                  </>
                )}
                <div className="flex flex-col items-center gap-1">
                  <CountdownDigit value={parts.hours} accent={accent} />
                  <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">часов</span>
                </div>
                <span className="mb-5 text-xl font-bold" style={{ color: accent }}>:</span>
                <div className="flex flex-col items-center gap-1">
                  <CountdownDigit value={parts.minutes} accent={accent} />
                  <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">минут</span>
                </div>
                <span className="mb-5 text-xl font-bold" style={{ color: accent }}>:</span>
                <div className="flex flex-col items-center gap-1">
                  <CountdownDigit value={parts.seconds} accent={accent} />
                  <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">секунд</span>
                </div>
              </div>
            ) : null}
          </div>
        </div>
        {futureTotal !== null && typeof totalAmount === 'number' ? (
          <div className="grid min-w-[260px] grid-cols-2 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="p-4">
              <p className="text-xs text-slate-500">Сейчас</p>
              <p className="mt-1 text-xl font-bold text-slate-900">{formatCurrency(totalAmount)}</p>
            </div>
            <div className="border-l border-slate-100 bg-amber-50 p-4">
              <p className="text-xs text-amber-700">Через 6 месяцев</p>
              <p className="mt-1 text-xl font-bold text-amber-800">{formatCurrency(futureTotal)}</p>
              <p className="mt-1 text-[11px] text-amber-700">риск +30% к стоимости или объёму лечения</p>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  )
}


// ─── Messenger icons ──────────────────────────────────────────────────────────

function IconWhatsapp() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M20.52 3.48A11.8 11.8 0 0012.13 0C5.6 0 .29 5.31.29 11.84c0 2.09.55 4.13 1.59 5.93L.19 24l6.38-1.67a11.82 11.82 0 005.56 1.42h.01c6.53 0 11.84-5.31 11.84-11.84 0-3.16-1.23-6.14-3.46-8.43zM12.14 21.75h-.01a9.8 9.8 0 01-5-1.37l-.36-.21-3.78.99 1.01-3.68-.24-.38a9.83 9.83 0 01-1.5-5.26c0-5.43 4.42-9.84 9.86-9.84a9.8 9.8 0 016.96 2.89 9.79 9.79 0 012.89 6.96c0 5.43-4.42 9.85-9.84 9.85zm5.4-7.38c-.3-.15-1.75-.86-2.02-.96-.27-.1-.47-.15-.67.15-.2.3-.77.96-.94 1.16-.17.2-.35.22-.65.07-.3-.15-1.25-.46-2.38-1.47-.88-.78-1.47-1.75-1.64-2.05-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.61-.92-2.2-.24-.58-.49-.5-.67-.51l-.57-.01c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.22 3.08c.15.2 2.11 3.22 5.1 4.51.71.31 1.27.49 1.7.63.71.23 1.36.2 1.87.12.57-.09 1.75-.72 2-1.41.25-.69.25-1.29.17-1.41-.07-.13-.27-.2-.57-.35z" />
    </svg>
  )
}

function IconTelegram() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M21.94 4.16c.31-1.02-.67-1.87-1.58-1.5L2.48 9.96c-1.04.42-1.02 1.9.03 2.29l4.56 1.68 1.74 5.58c.32 1.02 1.62 1.32 2.35.54l2.5-2.66 4.62 3.41c.85.63 2.06.16 2.29-.87l3.36-15.77zM8.1 13.12l9.54-5.88-7.88 7.45-.31 3.28-1.35-4.85z" />
    </svg>
  )
}

function IconPhone() {
  return (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
  )
}

// ─── Clinic contacts panel ────────────────────────────────────────────────────

function ClinicContactsPanel({
  bootstrap,
  accent,
  onEvent,
}: {
  bootstrap: TenantBootstrapModel
  accent: string
  onEvent: (event: string, payload?: Record<string, unknown>) => void
}) {
  const clinic = bootstrap.clinic
  if (!clinic) return null

  const qrTarget = clinic.whatsappUrl || `tel:${clinic.phoneTel}`
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(qrTarget)}`

  return (
    <section className="rounded-[28px] border border-slate-200/70 bg-white p-6 shadow-[0_18px_42px_rgba(15,23,42,0.08)]">
      <h2 className="text-xl font-semibold text-slate-900">Связь с клиникой</h2>
      <p className="mt-2 text-sm leading-6 text-slate-500">
        {clinic.workHours ?? 'Для записи и уточнения деталей обратитесь в клинику.'}
      </p>

      <div className="mt-5 flex flex-col items-center gap-4">
        {/* QR */}
        <img src={qrSrc} alt="QR для связи" width={140} height={140} className="rounded-lg border border-slate-200" loading="lazy" />
        <p className="text-xs text-slate-500">Отсканируйте, чтобы написать</p>

        {/* Phone */}
        {clinic.phone ? (
          <a
            href={`tel:${clinic.phoneTel}`}
            onClick={() => onEvent('click', { target: 'phone', channel: 'phone', label: clinic.phone })}
            className="flex items-center gap-2 text-sm font-semibold hover:opacity-80"
            style={{ color: accent }}
          >
            <IconPhone />
            {clinic.phone}
          </a>
        ) : null}

        {/* Messenger buttons */}
        <div className="flex w-full flex-wrap justify-center gap-2">
          {clinic.whatsappUrl ? (
            <a
              href={clinic.whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="WhatsApp"
              title={clinic.whatsappLabel ?? 'WhatsApp'}
              onClick={() => onEvent('click', { target: 'messenger:whatsapp', channel: 'whatsapp' })}
              className="flex h-11 w-11 items-center justify-center rounded-xl bg-green-600 text-white hover:bg-green-700"
            >
              <IconWhatsapp />
            </a>
          ) : null}

          {clinic.telegramUrl ? (
            <a
              href={clinic.telegramUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={clinic.telegramLabel ?? 'Telegram'}
              title={clinic.telegramLabel ?? 'Telegram'}
              onClick={() => onEvent('click', { target: 'messenger:telegram', channel: 'telegram' })}
              className="flex h-11 w-11 items-center justify-center rounded-xl bg-sky-500 text-white hover:bg-sky-600"
            >
              <IconTelegram />
            </a>
          ) : null}

          {clinic.telegramNotifyUrl ? (
            <a
              href={clinic.telegramNotifyUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={clinic.telegramNotifyLabel ?? 'Telegram бот'}
              title={clinic.telegramNotifyLabel ?? 'Telegram бот'}
              onClick={() => onEvent('click', { target: 'messenger:telegram_bot', channel: 'telegram' })}
              className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-600 text-white hover:bg-blue-700"
            >
              <IconTelegram />
            </a>
          ) : null}

          {clinic.maxBotUrl ? (
            <a
              href={clinic.maxBotUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={clinic.maxBotLabel ?? 'MAX'}
              title={clinic.maxBotLabel ?? 'MAX'}
              onClick={() => onEvent('click', { target: 'messenger:max', channel: 'max' })}
              className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-xl bg-violet-600 text-white hover:bg-violet-700"
            >
              <img src={`${import.meta.env.BASE_URL}max-logo.png`} alt="" className="h-7 w-7 rounded-[4px]" aria-hidden />
            </a>
          ) : null}
        </div>

        {clinic.address ? (
          <p className="text-center text-xs leading-relaxed text-slate-500">{clinic.address}</p>
        ) : null}
      </div>
    </section>
  )
}

// ─── Tooth data ───────────────────────────────────────────────────────────────

interface TeethData {
  display: string[]
  numeric: number[]
  servicesByTooth: Record<number, string[]>
}

function readServiceToothNumber(svc: MisPlanService): string {
  const extra = svc as MisPlanService & { tooth?: string | null; teeth?: Array<string | number> | null }
  const direct = (svc.tooth_number ?? extra.tooth ?? "").trim()
  if (direct) return direct
  const teeth = extra.teeth
  if (Array.isArray(teeth) && teeth.length > 0) return String(teeth[0]).trim()
  return ""
}

function flattenPlanServices(plan: MisPlanData | null | undefined): MisPlanService[] {
  if (!plan) return []
  const top = plan.services
  if (Array.isArray(top) && top.length > 0) return top
  const stages = (plan as MisPlanData & { stages?: Array<{ services?: MisPlanService[] | null }> }).stages
  if (!Array.isArray(stages)) return Array.isArray(top) ? top : []
  const merged: MisPlanService[] = []
  for (const stage of stages) {
    if (!Array.isArray(stage?.services)) continue
    for (const svc of stage.services) merged.push(svc)
  }
  return merged.length > 0 ? merged : (Array.isArray(top) ? top : [])
}

function collectTeeth(services: MisPlanService[]): TeethData {
  const display = new Set<string>()
  const numeric = new Set<number>()
  const servicesByTooth: Record<number, string[]> = {}

  for (const svc of services) {
    const raw = readServiceToothNumber(svc)
    if (!raw || raw === '-' || raw === '—' || raw === '0') continue
    display.add(raw)
    const num = Number.parseInt(raw, 10)
    if (Number.isFinite(num) && num > 0) {
      numeric.add(num)
      const label = svc.service_name ?? ''
      const cur = servicesByTooth[num] ?? []
      if (!cur.includes(label)) servicesByTooth[num] = [...cur, label]
    }
  }

  return {
    display: Array.from(display).sort((a, b) => Number(a) - Number(b)),
    numeric: Array.from(numeric).sort((a, b) => a - b),
    servicesByTooth,
  }
}

// ─── API helpers ─────────────────────────────────────────────────────────────

interface MisPlanService {
  id?: number | string
  service_name?: string
  tooth_number?: string | null
  quantity?: number | null
  unit_price?: number | null
  total_price?: number | null
  original_price?: number | null
  is_completed?: boolean
}

interface MisPlanData {
  title?: string
  patient_name?: string
  doctor_name?: string
  plan_date?: string | null
  status?: string
  total_amount?: number | null
  services?: MisPlanService[]
  note?: string | null
  discount_percent?: number | null
  offer_deadline?: string | null
  hide_prices?: boolean
  updated_at?: string | null
  created_at?: string | null
  payment?: {
    providers?: { yookassa?: boolean; yandex_pay?: boolean }
    yandex_pay_split?: boolean
    cash_special?: boolean
    partial?: {
      enabled?: boolean
      min_rub?: number | null
      max_rub?: number | null
      presets?: Array<'30' | '50' | '100'>
    } | null
  } | null
}

interface PlanFailure { status: string; error: string; has_password?: boolean }

async function fetchMisPlan(apiBase: string, token: string, password?: string): Promise<MisPlanData> {
  const base = `${apiBase}/public/mis-plans/${encodeURIComponent(token)}`
  const url = password ? `${base}?password=${encodeURIComponent(password)}` : base
  const res = await fetch(url, { headers: { Accept: 'application/json' } })
  const json = await res.json() as { success?: boolean; data?: MisPlanData; status?: string; error?: string; has_password?: boolean }
  if (!res.ok || !json.success) {
    const err = Object.assign(new Error(json.error ?? 'Не удалось загрузить план.'), {
      details: {
        status: json.status ?? 'not_found',
        error: json.error ?? 'Публичный MIS-план недоступен.',
        has_password: json.has_password ?? false,
      } satisfies PlanFailure,
    })
    throw err
  }
  return json.data as MisPlanData
}

function recordMisEvent(apiBase: string, token: string, event: string, payload: Record<string, unknown> = {}) {
  const url = `${apiBase}/public/mis-plans/${encodeURIComponent(token)}/events`
  const body = JSON.stringify({ event, ...payload })
  if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
    navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }))
    return
  }
  void fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true })
}

// ─── Page component ───────────────────────────────────────────────────────────

export function PublicMisPlanPage({
  bootstrap,
  publicToken,
}: {
  bootstrap: TenantBootstrapModel
  publicToken: string
}) {
  const [plan, setPlan] = useState<MisPlanData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<PlanFailure | null>(null)
  // Password gate state
  const [needsPassword, setNeedsPassword] = useState(false)
  const [passwordInput, setPasswordInput] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [passwordChecking, setPasswordChecking] = useState(false)
  const openedAtRef = useRef<number>(Date.now())
  const maxScrollDepthRef = useRef<number>(0)
  const closeSentRef = useRef(false)

  const accent = bootstrap.branding.primaryColor || '#1aa8d6'

  const onEvent = (event: string, payload?: Record<string, unknown>) => {
    recordMisEvent(bootstrap.apiBase, publicToken, event, payload)
  }

  function loadPlan(password?: string) {
    let mounted = true
    setLoading(true)
    setError(null)
    fetchMisPlan(bootstrap.apiBase, publicToken, password)
      .then((data) => {
        if (!mounted) return
        setNeedsPassword(false)
        setPlan(data)
        openedAtRef.current = Date.now()
        recordMisEvent(bootstrap.apiBase, publicToken, 'view', {
          user_agent: navigator.userAgent,
          device_type: window.innerWidth < 768 ? 'mobile' : 'desktop',
        })
      })
      .catch((reason: Error & { details?: PlanFailure }) => {
        if (!mounted) return
        const details = reason.details ?? { status: 'not_found', error: reason.message || 'Публичный MIS-план недоступен.' }
        if (details.status === 'password_required') {
          setNeedsPassword(true)
          setLoading(false)
          return
        }
        setError(details)
      })
      .finally(() => { if (mounted) setLoading(false) })
    return () => { mounted = false }
  }

  async function handlePasswordSubmit(e: React.FormEvent) {
    e.preventDefault()
    const pw = passwordInput.trim()
    if (!pw) { setPasswordError('Введите пароль'); return }
    setPasswordChecking(true)
    setPasswordError('')
    try {
      await fetchMisPlan(bootstrap.apiBase, publicToken, pw)
        .then((data) => {
          setNeedsPassword(false)
          setPasswordInput('')
          setPlan(data)
          openedAtRef.current = Date.now()
          recordMisEvent(bootstrap.apiBase, publicToken, 'view', {
            user_agent: navigator.userAgent,
            device_type: window.innerWidth < 768 ? 'mobile' : 'desktop',
          })
        })
    } catch (reason) {
      const details = (reason as Error & { details?: PlanFailure }).details
      if (details?.status === 'password_required') {
        setPasswordError('Неверный пароль. Попробуйте ещё раз.')
      } else {
        setPasswordError(details?.error ?? 'Не удалось загрузить план.')
      }
    } finally {
      setPasswordChecking(false)
    }
  }

  useEffect(() => {
    return loadPlan()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bootstrap.apiBase, publicToken])

  useEffect(() => {
    function updateScroll() {
      const h = document.documentElement.scrollHeight - window.innerHeight
      if (h <= 0) { maxScrollDepthRef.current = 100; return }
      maxScrollDepthRef.current = Math.max(maxScrollDepthRef.current, Math.round((window.scrollY / h) * 100))
    }
    function flush() {
      if (closeSentRef.current || !plan) return
      closeSentRef.current = true
      recordMisEvent(bootstrap.apiBase, publicToken, 'close', {
        time_on_page: Date.now() - openedAtRef.current,
        scroll_depth_percent: maxScrollDepthRef.current,
      })
    }
    window.addEventListener('scroll', updateScroll, { passive: true })
    window.addEventListener('pagehide', flush)
    return () => { window.removeEventListener('scroll', updateScroll); window.removeEventListener('pagehide', flush); flush() }
  }, [bootstrap.apiBase, plan, publicToken])

  const discountedTotal = useMemo(() => {
    if (!plan?.total_amount || !plan?.discount_percent) return null
    return plan.total_amount * (1 - plan.discount_percent / 100)
  }, [plan?.total_amount, plan?.discount_percent])

  // Original total from CCS line items (pre-discount) if any items have original_price.
  // Only count services that have a non-zero effective price to avoid skewing the sum
  // with fully-discounted/replaced items (total_price=0) that still carry an original_price.
  const originalTotal = useMemo(() => {
    const services = plan?.services ?? []
    const hasOriginals = services.some((s) => s.original_price && s.total_price && s.original_price > s.total_price)
    if (!hasOriginals) return null
    return services.reduce((sum, s) => {
      const tp = s.total_price ?? 0
      if (tp <= 0) return sum  // skip zero-price/cancelled services
      return sum + (s.original_price ?? tp)
    }, 0)
  }, [plan?.services])

  const planServices = useMemo(() => flattenPlanServices(plan), [plan])
  const teeth = useMemo(() => collectTeeth(planServices), [planServices])
  const ALL_TEETH = [11,12,13,14,15,16,17,18,21,22,23,24,25,26,27,28,31,32,33,34,35,36,37,38,41,42,43,44,45,46,47,48]

  const clinic = bootstrap.clinic

  return (
    <div
      className="min-h-screen bg-[linear-gradient(180deg,#f8fbff_0%,#f5f6f8_100%)] text-slate-900"
      style={{ ['--public-accent' as string]: accent }}
    >
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <header className="border-b border-slate-200/70 bg-white/95 shadow-sm">
        <div className="mx-auto flex max-w-[1240px] items-center justify-between gap-4 px-4 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            {clinic?.publicLogoUrl ? (
              <img src={clinic.publicLogoUrl} alt={bootstrap.branding.appName} className="h-10 w-auto object-contain" />
            ) : (
              <span className="text-lg font-bold" style={{ color: accent }}>{bootstrap.branding.appName}</span>
            )}
          </div>
          <div className="hidden flex-col items-end text-right sm:flex">
            {clinic?.phone ? (
              <a href={`tel:${clinic.phoneTel}`} className="text-sm font-semibold hover:opacity-80" style={{ color: accent }}>
                {clinic.phone}
              </a>
            ) : null}
            {clinic?.workHours ? (
              <span className="text-xs text-slate-500">{clinic.workHours}</span>
            ) : null}
          </div>
        </div>
      </header>

      <div className="mx-auto grid max-w-[1240px] gap-5 px-4 py-6 sm:px-6">

        {loading ? (
          <section className="rounded-[28px] border border-slate-200/70 bg-white p-6 text-sm leading-6 text-slate-500 shadow-[0_18px_42px_rgba(15,23,42,0.08)]">
            <p>Загружаем план лечения...</p>
          </section>
        ) : needsPassword ? (
          <section className="mx-auto w-full max-w-sm rounded-[28px] border border-slate-200/70 bg-white p-8 shadow-[0_18px_42px_rgba(15,23,42,0.08)]">
            <div className="mb-6 flex flex-col items-center gap-3 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full" style={{ background: `${accent}18` }}>
                <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: accent }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h1 className="text-xl font-semibold text-slate-900">Доступ по паролю</h1>
              <p className="text-sm leading-5 text-slate-500">
                Врач ограничил доступ к этому плану лечения.<br />Введите пароль, чтобы открыть план.
              </p>
            </div>
            <form onSubmit={(e) => void handlePasswordSubmit(e)} className="grid gap-3">
              <input
                type="password"
                autoFocus
                autoComplete="current-password"
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-900 outline-none transition focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
                placeholder="Пароль"
                value={passwordInput}
                onChange={(e) => { setPasswordInput(e.target.value); setPasswordError('') }}
                disabled={passwordChecking}
              />
              {passwordError && (
                <p className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700">{passwordError}</p>
              )}
              <button
                type="submit"
                disabled={passwordChecking || !passwordInput.trim()}
                className="w-full rounded-2xl py-3 text-sm font-semibold text-white transition disabled:opacity-50"
                style={{ background: accent }}
              >
                {passwordChecking ? 'Проверяем...' : 'Открыть план'}
              </button>
            </form>
          </section>
        ) : error ? (
          <section className="rounded-[28px] border border-rose-200 bg-white p-10 text-center shadow-[0_18px_42px_rgba(15,23,42,0.08)]">
            <h1 className="text-2xl font-semibold text-slate-900">
              {error.status === 'expired' ? 'Срок ссылки истёк' : error.status === 'inactive' ? 'Ссылка деактивирована' : 'Не найдено'}
            </h1>
            <p className="mt-2 text-sm leading-6 text-rose-700">{error.error}</p>
          </section>
        ) : plan ? (
          <main className="grid gap-5">

            {/* ── Plan card ─── */}
            <section className="rounded-[28px] border border-slate-200/70 bg-white p-6 shadow-[0_18px_42px_rgba(15,23,42,0.08)]">
              <p className="text-xs font-bold uppercase tracking-[0.14em]" style={{ color: accent }}>
                {clinic?.legalDetails ?? bootstrap.branding.appName} · План лечения
              </p>
              <h1 className="mt-2 text-3xl font-semibold text-slate-900">{plan.title ?? 'План лечения'}</h1>
              {plan.note ? <p className="mt-3 text-sm leading-6 text-slate-600">{plan.note}</p> : null}

              <div className="mt-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <div className="grid gap-1 rounded-[20px] border border-slate-200/70 bg-slate-50 px-4 py-4">
                  <span className="text-xs text-slate-500">Пациент</span>
                  <strong className="text-slate-900">{plan.patient_name || 'Не указано'}</strong>
                </div>
                <div className="grid gap-1 rounded-[20px] border border-slate-200/70 bg-slate-50 px-4 py-4">
                  <span className="text-xs text-slate-500">Лечащий врач</span>
                  <strong className="text-slate-900">{plan.doctor_name ?? 'Не указан'}</strong>
                  <small className="text-slate-500">Дата: {formatDate(plan.plan_date ?? plan.created_at)}</small>
                </div>
                <div className="grid gap-1 rounded-[20px] border border-slate-200/70 bg-slate-50 px-4 py-4">
                  <span className="text-xs text-slate-500">Позиций</span>
                  <strong className="text-slate-900">{planServices.length}</strong>
                </div>
                <div className="grid gap-1 rounded-[20px] border border-slate-200/70 bg-slate-50 px-4 py-4">
                  <span className="text-xs text-slate-500">Стоимость</span>
                  {plan.hide_prices ? (
                    <strong className="text-slate-900">По запросу</strong>
                  ) : (
                    <>
                      <strong className="text-slate-900">{formatCurrency(plan.total_amount)}</strong>
                      {originalTotal && originalTotal > (plan.total_amount ?? 0) ? (
                        <small className="text-emerald-600">
                          экономия {formatCurrency(originalTotal - (plan.total_amount ?? 0))}
                          <span className="ml-1 text-slate-400 line-through">{formatCurrency(originalTotal)}</span>
                        </small>
                      ) : null}
                      {discountedTotal ? (
                        <small className="text-emerald-600">со скидкой {plan.discount_percent}%: {formatCurrency(discountedTotal)}</small>
                      ) : null}
                    </>
                  )}
                </div>
              </div>
            </section>

            {/* ── Discount banner ─── */}
            {plan.discount_percent && !plan.hide_prices ? (
              <div className="rounded-[28px] border border-emerald-200 bg-emerald-50 p-5 shadow-[0_18px_42px_rgba(15,23,42,0.04)]">
                <p className="text-xs font-bold uppercase tracking-[0.14em] text-emerald-600">Специальное предложение</p>
                <p className="mt-2 text-slate-700">
                  Скидка <strong className="text-emerald-700">{plan.discount_percent}%</strong> при оплате
                  {plan.offer_deadline ? ` до ${formatDate(plan.offer_deadline)}` : ''}.
                </p>
                <p className="mt-1 text-lg font-semibold text-slate-900">
                  Итого со скидкой: {formatCurrency(discountedTotal)}
                </p>
              </div>
            ) : null}

            {/* ── Tooth chart + Contacts (side by side on lg) ─── */}
            <div className="grid gap-5 lg:grid-cols-[minmax(0,1.4fr)_340px]">

              {/* Tooth chart */}
              <section className="rounded-[28px] border border-slate-200/70 bg-white p-6 shadow-[0_18px_42px_rgba(15,23,42,0.08)]">
                <h2 className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500">Зубная формула</h2>

                {teeth.display.length > 0 ? (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {teeth.display.map((t) => (
                      <span
                        key={t}
                        className="inline-flex h-7 min-w-[28px] items-center justify-center rounded border px-1.5 text-xs font-semibold"
                        style={{ borderColor: accent + '66', color: accent, backgroundColor: accent + '12' }}
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                ) : (
                  <div
                    className="mt-3 inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium"
                    style={{ borderColor: accent + '66', color: accent, backgroundColor: accent + '12' }}
                  >
                    Вся полость рта
                  </div>
                )}

                <div className="mt-4 overflow-x-auto">
                  <ToothChart
                    highlighted={teeth.numeric.length > 0 ? teeth.numeric : ALL_TEETH}
                    toothServices={teeth.servicesByTooth}
                    primaryColor={accent}
                    legend={false}
                  />
                </div>
              </section>

              {/* Clinic contacts */}
              <ClinicContactsPanel bootstrap={bootstrap} accent={accent} onEvent={onEvent} />
            </div>

            {/* ── Countdown (below tooth chart on lg) ─── */}
            {plan.offer_deadline ? (
              <OfferCountdownBadge
                deadline={plan.offer_deadline}
                accent={accent}
                totalAmount={plan.total_amount}
                hidePrices={plan.hide_prices}
              />
            ) : null}

            {/* ── Services table ─── */}
            <section className="rounded-[28px] border border-slate-200/70 bg-white p-6 shadow-[0_18px_42px_rgba(15,23,42,0.08)]">
              <div className="flex items-start justify-between gap-3">
                <h2 className="text-2xl font-semibold text-slate-900">Услуги</h2>
                <span className="text-sm text-slate-500">{planServices.length} поз.</span>
              </div>

              <div className="mt-5 grid gap-px overflow-hidden rounded-[24px] border border-slate-200/70 bg-slate-200">
                <div className="hidden grid-cols-[90px_minmax(0,1.2fr)_60px_140px] gap-4 bg-slate-50 px-4 py-3 text-xs font-semibold uppercase tracking-[0.08em] text-slate-500 lg:grid">
                  <span>Зуб</span><span>Услуга</span><span>Кол-во</span><span>Стоимость</span>
                </div>
                {planServices.length > 0 ? planServices.map((svc, i) => (
                  <div
                    key={svc.id ?? i}
                    className="grid gap-3 bg-white px-4 py-4 text-sm leading-6 text-slate-700 lg:grid-cols-[90px_minmax(0,1.2fr)_60px_140px] lg:items-start"
                  >
                    <span>
                      {(() => {
                        const t = (svc.tooth_number ?? '').trim()
                        return (!t || t === '-' || t === '—' || t === '0')
                          ? <span className="text-slate-400">Вся полость</span>
                          : <span
                              className="inline-flex h-7 min-w-[28px] items-center justify-center rounded border px-1.5 text-xs font-semibold"
                              style={{ borderColor: accent + '66', color: accent, backgroundColor: accent + '12' }}
                            >{t}</span>
                      })()}
                    </span>
                    <span>{svc.service_name ?? '—'}</span>
                    <span>{svc.quantity ?? 1}</span>
                    <span className="flex flex-col gap-0.5">
                      {plan.hide_prices ? (
                        <strong className="text-slate-900">По запросу</strong>
                      ) : (
                        <>
                          <strong className="text-slate-900">{formatCurrency(svc.total_price ?? svc.unit_price ?? null)}</strong>
                          {svc.original_price && svc.total_price && svc.original_price > svc.total_price ? (
                            <span className="text-xs text-slate-400 line-through">{formatCurrency(svc.original_price)}</span>
                          ) : null}
                        </>
                      )}
                    </span>
                  </div>
                )) : (
                  <div className="bg-white px-4 py-6 text-sm text-slate-400">Услуги не указаны.</div>
                )}
              </div>

              {!plan.hide_prices && (
                <div className="mt-4 flex items-center justify-end gap-4">
                  <span className="text-sm text-slate-500">Итого:</span>
                  <span className="flex flex-col items-end gap-0.5">
                    <strong className="text-xl text-slate-900">{formatCurrency(plan.total_amount)}</strong>
                    {originalTotal && originalTotal > (plan.total_amount ?? 0) ? (
                      <span className="text-xs text-slate-400 line-through">{formatCurrency(originalTotal)}</span>
                    ) : null}
                  </span>
                </div>
              )}
            </section>

          </main>
        ) : null}

        {/* ── Payment widget ─────────────────────────────────────────────── */}
        {plan?.payment && (plan.payment.providers?.yookassa || plan.payment.providers?.yandex_pay) ? (
          <section>
            <PublicPaymentWidget
              kind="mis-plans"
              token={publicToken}
              paymentConfig={plan.payment}
              totalAmountRub={plan.total_amount ?? null}
              accent={accent}
              apiBase={bootstrap.apiBase}
            />
          </section>
        ) : plan?.payment?.cash_special ? (
          <section className="rounded-[24px] border border-amber-200 bg-amber-50 p-5 shadow-sm">
            <div className="flex items-start gap-3">
              <span className="text-xl">💵</span>
              <div>
                <p className="text-sm font-semibold text-amber-900">Спецусловия при оплате наличными</p>
                <p className="mt-1 text-sm text-amber-800">
                  По этому плану действуют особые условия при оплате наличными или через СБП.
                  Уточните детали у администратора клиники.
                </p>
              </div>
            </div>
          </section>
        ) : null}

        {/* ── Footer ─────────────────────────────────────────────────────── */}
        <footer className="rounded-[28px] border border-slate-200/70 bg-white p-6 shadow-[0_18px_42px_rgba(15,23,42,0.08)]">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm leading-6 text-slate-500">
              {clinic?.legalName ? <p className="font-medium text-slate-700">{clinic.legalName}</p> : null}
              {clinic?.address ? <p>{clinic.address}</p> : null}
              {clinic?.email ? (
                <a href={`mailto:${clinic.email}`} className="hover:underline" style={{ color: accent }}>{clinic.email}</a>
              ) : null}
              {clinic?.website ? (
                <a href={clinic.website} target="_blank" rel="noopener noreferrer" className="ml-3 hover:underline" style={{ color: accent }}>
                  {clinic.websiteLabel ?? clinic.website}
                </a>
              ) : null}
            </div>
            <div className="shrink-0 text-right text-xs text-slate-400">
              <p>Публичный план сформирован клиникой.</p>
              <p>Обновлено: {formatDateTime(plan?.updated_at ?? plan?.created_at)}</p>
            </div>
          </div>
        </footer>

      </div>
    </div>
  )
}
